import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { createUserWithEmailAndPassword, updateProfile } from "firebase/auth";
import { auth } from "../../firebase";
import { createCheckoutSession, loadPlans, provisionFreePlan } from "../../api/subscriptions";

const SIGNUP_PROJECT_ID = process.env.REACT_APP_SIGNUP_PROJECT_ID;

async function reportClaimAttempt({ name, email, plan }) {
  const response = await fetch("https://go.switchbaq.com/events", {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-domain": "admin.bouncehouse.com",
    },
    body: JSON.stringify({
      name: "listing-claim-attempted",
      category: "listing",
      projectId: SIGNUP_PROJECT_ID,
      properties: {
        name,
        email,
        emailDomain: email.split("@").pop()?.trim().toLowerCase() || "",
        plan,
      },
      source: {
        system: "bouncehouse-admin",
        campaign: "listing-claim",
      },
      occurredAt: new Date().toISOString(),
    }),
    credentials: "omit",
  });

  if (!response.ok) throw new Error(`Claim attempt event failed (${response.status})`);
}

export default function Signup() {
  const { search } = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const plan = params.get("plan") || "custom";
  const isFree = plan.toLowerCase() === "free";
  const checkout = params.get("checkout");

  useEffect(() => {
    if (plan === "custom") {
      window.location.replace("https://bouncehouse.com/businesses/custom-website-design");
    }
  }, [plan]);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [domain, setDomain] = useState("");
  const [status, setStatus] = useState({ message: "", error: false });
  const [claimBlocked, setClaimBlocked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (plan === "custom") return null;

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    setStatus({ message: "", error: false });
    setClaimBlocked(false);

    try {
      if (plan !== "custom") {
        if (!SIGNUP_PROJECT_ID) throw new Error("Signup billing is missing its project configuration.");
        const plansResponse = await loadPlans();
        const plans = Array.isArray(plansResponse?.result) ? plansResponse.result : [];
        const selectedPlan = plans.find((item) =>
          item?.active !== false &&
          (String(item?.id || "").toLowerCase() === plan.toLowerCase() ||
            String(item?.name || "").toLowerCase() === plan.toLowerCase())
        );
        if (!selectedPlan) throw new Error(`The ${plan} plan is not configured.`);
        if (!isFree && !selectedPlan.priceId) throw new Error(`The ${plan} plan is not configured for Stripe Checkout.`);

        try {
          await reportClaimAttempt({
            name: name.trim(),
            email: email.trim(),
            plan,
          });
        } catch (eventError) {
          console.warn("Unable to report listing-claim-attempted event", eventError);
        }

        const credential = await createUserWithEmailAndPassword(auth, email.trim(), password);
        if (name.trim()) await updateProfile(credential.user, { displayName: name.trim() });
        await credential.user.getIdToken(true);

        try {
          if (isFree) {
            await provisionFreePlan({ planId: selectedPlan.id, projectId: SIGNUP_PROJECT_ID });
            window.location.assign("/verify-email");
            return;
          }

          const baseUrl = window.location.origin;
          const response = await createCheckoutSession({
            uid: credential.user.uid,
            priceId: selectedPlan.priceId,
            planId: selectedPlan.id,
            projectId: SIGNUP_PROJECT_ID,
            successUrl: `${baseUrl}/login?checkout=success`,
            cancelUrl: `${baseUrl}/signup?checkout=cancelled&plan=${encodeURIComponent(plan)}`,
          });
          const checkoutUrl = response?.result?.url;
          if (!checkoutUrl) throw new Error("Stripe Checkout did not return a URL.");
          window.location.assign(checkoutUrl);
          return;
        } catch (claimError) {
          if (claimError?.data?.error?.code === "CANNOT_PROCESS") {
            // Remove the unusable login so the visitor can try again with another email.
            await credential.user.delete().catch(() => {});
            setClaimBlocked(true);
          }
          throw claimError;
        }
      }

      const body = {
        projectId: SIGNUP_PROJECT_ID,
        formName: "bouncehouse-admin-learn-more",
        name: name.trim(),
        email: email.trim(),
        source: { campaign: "bhouse-admin-custom-plan" },
      };
      if (domain.trim()) body.domain = domain.trim();

      const res = await fetch("https://go.switchbaq.com/activities", {
        method: "POST",
        headers: { "content-type": "application/json", "x-domain": "admin.bouncehouse.com" },
        body: JSON.stringify(body),
        credentials: "omit",
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || data?.message || `Submit failed (${res.status})`);
      }
      setStatus({ message: "Thanks — we’ll follow up soon.", error: false });
      setName("");
      setEmail("");
      setDomain("");
    } catch (err) {
      setStatus({ message: err instanceof Error ? err.message : "Submission failed.", error: true });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-12 col-sm-10 col-md-7 col-lg-6">
          <div className="text-center mb-4">
            <h1 className="fw-bold mb-2">{plan === "custom" ? "Ready for a custom website design? We can help!" : `Start your ${plan} plan`}</h1>
            <p className="text-muted mb-0">
              {plan === "custom"
                ? "Fill out the form and we’ll follow up with you."
                : isFree
                  ? "Sign up with your business email address. We'll email you a code to confirm you own the listing."
                  : "Sign up with your business email address, then complete payment securely with Stripe."}
            </p>
          </div>

          {checkout === "success" ? (
            <div className="text-center">
              <div className="alert alert-success">Payment completed. Your account is being activated.</div>
              <p className="text-muted">You do not need to register again.</p>
              <a className="btn btn-primary" href="/posts/attraction">Continue to your account</a>
            </div>
          ) : null}
          {checkout === "cancelled" ? <div className="alert alert-warning">Checkout was cancelled. You can try again below.</div> : null}
          {status.message ? (
            <div className={`alert ${status.error ? "alert-danger" : "alert-success"}`}>
              {status.message}
              {claimBlocked ? <div className="mt-2"><a href="mailto:help@bouncehouse.com">help@bouncehouse.com</a></div> : null}
            </div>
          ) : null}

          {checkout !== "success" ? <div className="card shadow-sm border-0">
            <div className="card-body p-4 p-md-5">
              <form onSubmit={submit}>
                <div className="mb-3 text-start">
                  <label className="form-label">Name</label>
                  <input className="form-control" value={name} onChange={(e) => setName(e.target.value)} required />
                </div>
                <div className="mb-3 text-start">
                  <label className="form-label">Email</label>
                  <input type="email" className="form-control" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required />
                </div>
                {plan !== "custom" ? (
                  <div className="mb-3 text-start">
                    <label className="form-label">Password</label>
                    <input type="password" className="form-control" value={password} onChange={(e) => setPassword(e.target.value)} minLength={6} autoComplete="new-password" required />
                  </div>
                ) : null}
                {plan === "custom" ? <div className="mb-3 text-start">
                  <label className="form-label">Website domain (optional)</label>
                  <input type="url" className="form-control" value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="https://your-domain.com" />
                </div> : null}
                <button type="submit" className="btn btn-primary w-100" disabled={submitting}>
                  {submitting ? "Please wait…" : plan === "custom" ? "Submit" : isFree ? "Create Free Account" : "Continue to secure checkout"}
                </button>
              </form>
            </div>
          </div> : null}
          {checkout !== "success" ? <div className="text-center text-muted mt-3">Selected plan: <strong>{plan}</strong></div> : null}
        </div>
      </div>
    </div>
  );
}
